import type { Customer, User } from '@/types/user';
import rest from '@/lib/rest';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const INITIAL_USER: User = {
  id: '',
  email: '',
  name: '',
  stripeId: '',
  checkoutId: '',
  uid: '',
  createdAt: 0,
  emailVerified: false,
  lastLoginAt: 0,
  passwordUpdatedAt: 0,
  validSince: 0,
  claims: { admin: false },
  photoUrl: '',
  status: '',
  balance: 0,
};

export const useUserStore = defineStore('user', () => {
  const user = ref<User>({ ...INITIAL_USER });

  const isLoading = ref(false);

  function setUser(next: Partial<User>) {
    user.value = { ...INITIAL_USER, ...next };
  }

  function setStripeId(stripeId: string) {
    user.value.stripeId = stripeId;
  }

  function clear() {
    user.value = { ...INITIAL_USER };
  }

  async function updateAccount({ name, email, photoUrl }: { name: string; email: string; photoUrl?: string }) {
    const customer: Partial<Customer> & { photoUrl?: string } = {
      uid: user.value.uid,
      stripeId: user.value.stripeId,
      email,
      name,
    };
    if (photoUrl !== undefined) customer.photoUrl = photoUrl;
    const response = await rest
      .put(`auth/user`)
      .header('x-uid', user.value.uid || '')
      .credentials('include')
      .send(customer);

    if (!response.ok) {
      throw new Error('Failed to update account');
    }

    user.value.name = name;
    user.value.email = email;
    if (photoUrl !== undefined) user.value.photoUrl = photoUrl;
  }

  /**
   * Profile-page "Connect Google": attach the Google identity in `credential`
   * to the signed-in account (POST /auth/google/link). The session authorizes
   * the link, so the Google email does NOT need to match the account email.
   * Errors surface the API code (e.g. GOOGLE_ACCOUNT_IN_USE) to the caller.
   */
  async function linkGoogle(credential: string) {
    const response = await rest
      .post('auth/google/link')
      .header('x-uid', user.value.uid || '')
      .credentials('include')
      .send({ credential });

    if (!response.user) {
      throw new Error('Failed to link Google account');
    }
    user.value = { ...user.value, ...response.user };
  }

  async function updatePassword(email: string, currentPassword: string, newPassword: string) {
    isLoading.value = true;
    try {
      const response = await rest
        .put(`auth/user`)
        .header('x-uid', user.value.uid || '')
        .credentials('include')
        .send({
          email: email,
          password: newPassword,
          oldPassword: currentPassword,
        });

      if (!response.ok) {
        throw new Error('Failed to update password');
      }
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchUser(uid: string) {
    isLoading.value = true;
    try {
      const response = await rest.get('/auth/user').header('x-uid', uid).credentials('include').send();

      if (!response.uid) {
        throw new Error('Failed to fetch user');
      }
      user.value = { ...INITIAL_USER, ...response };
    } finally {
      isLoading.value = false;
    }
  }

  async function requestPasswordReset(email: string) {
    isLoading.value = true;
    try {
      const response = await rest.post(`auth/passwordReset`).credentials('include').send({ email });

      if (!response.ok) {
        throw new Error('Failed to request password reset');
      }
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteUser(user: User) {
    await rest
      .delete(`/auth/user`)
      .header('x-uid', user.uid || '')
      .query('immediately', 'true')
      .credentials('include')
      .send();
  }

  /**
   * Send (or resend) the email-verification link to the signed-in account's
   * address. Backs the "Send verification email" affordance shown to unverified
   * password accounts, and lets a user clear the EMAIL_NOT_VERIFIED gate that
   * blocks linking Google (see auth-api-error-codes).
   */
  async function sendVerification() {
    const response = await rest
      .post(`auth/accounts/${user.value.uid}/sendVerification`)
      .header('x-uid', user.value.uid || '')
      .credentials('include')
      .send();

    if (!response.ok) {
      throw new Error('Failed to send verification email');
    }
  }

  return {
    user,
    isLoading,
    setUser,
    setStripeId,
    clear,
    updateAccount,
    updatePassword,
    fetchUser,
    linkGoogle,
    requestPasswordReset,
    sendVerification,
    deleteUser,
  };
});
